/**
 * System Tags Section - New and Unused tags, plus cache settings.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Save, Sparkles, Database } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { Switch } from '@/components/ui';
import { ColorPicker } from '@/components/ui';
import {
  useListTagRulesSettings,
  useUpdateListTagRulesSettings,
} from '@/hooks';
import { useToast } from '@/providers/ToastProvider';
import { getApiErrorMessage } from '@/lib/utils';
import { cn } from '@/lib/utils';

import {
  datetimeLocalValueToIsoOrNull,
  isoToDatetimeLocalValue,
  unusedSinceTimestampsEqual,
} from '../utils';

const CACHE_TTL_MIN = 1;
const CACHE_TTL_MAX = 168;

const TAG_COLOR_PRESETS = [
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#22c55e',
  '#14b8a6',
  '#6b7280',
  '#f59e0b',
  '#ef4444',
] as const;

export const SystemTagsSection: FC = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const { data: settings, isLoading } = useListTagRulesSettings();
  const updateSettings = useUpdateListTagRulesSettings();
  const [newDays, setNewDays] = useState<string>('2');
  const [newColor, setNewColor] = useState<string>('#3b82f6');
  const [unusedColor, setUnusedColor] = useState<string>('#6b7280');
  const [unusedSinceLocal, setUnusedSinceLocal] = useState<string>('');
  const [cacheTtlHours, setCacheTtlHours] = useState<string>('12');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings?.newDays != null) setNewDays(String(settings.newDays));
  }, [settings?.newDays]);

  useEffect(() => {
    if (settings?.newColor != null) setNewColor(settings.newColor);
  }, [settings?.newColor]);

  useEffect(() => {
    if (settings?.unusedColor != null) setUnusedColor(settings.unusedColor);
  }, [settings?.unusedColor]);

  useEffect(() => {
    if (settings?.unusedSinceIso !== undefined) {
      setUnusedSinceLocal(isoToDatetimeLocalValue(settings.unusedSinceIso));
    }
  }, [settings?.unusedSinceIso]);

  useEffect(() => {
    if (settings?.cacheTtlHours != null) setCacheTtlHours(String(settings.cacheTtlHours));
  }, [settings?.cacheTtlHours]);

  const parsedDays = Math.max(1, Math.min(365, parseInt(newDays, 10) || 2));
  const parsedTtl = Math.max(CACHE_TTL_MIN, Math.min(CACHE_TTL_MAX, parseInt(cacheTtlHours, 10) || 12));
  const daysValid = /^\d+$/.test(newDays.trim()) && parsedDays >= 1 && parsedDays <= 365;
  const ttlValid =
    /^\d+$/.test(cacheTtlHours.trim()) &&
    parsedTtl >= CACHE_TTL_MIN &&
    parsedTtl <= CACHE_TTL_MAX;
  const hexValid = (v: string) => /^#[0-9a-fA-F]{3,8}$/.test(String(v || '').trim());

  const proposedUnusedIso = datetimeLocalValueToIsoOrNull(unusedSinceLocal);
  const unusedSinceInputValid = !unusedSinceLocal.trim() || proposedUnusedIso !== null;

  const hasAnyChange = useCallback(() => {
    const daysChanged = daysValid && parsedDays !== settings?.newDays;
    const ttlChanged = ttlValid && parsedTtl !== settings?.cacheTtlHours;
    const newColorChanged = hexValid(newColor) && newColor !== settings?.newColor;
    const unusedColorChanged = hexValid(unusedColor) && unusedColor !== settings?.unusedColor;
    const unusedSinceChanged =
      unusedSinceInputValid &&
      !unusedSinceTimestampsEqual(proposedUnusedIso ?? '', settings?.unusedSinceIso ?? '');
    return Boolean(
      daysChanged || ttlChanged || newColorChanged || unusedColorChanged || unusedSinceChanged,
    );
  }, [
    daysValid,
    parsedDays,
    settings?.newDays,
    ttlValid,
    parsedTtl,
    settings?.cacheTtlHours,
    newColor,
    unusedColor,
    settings?.newColor,
    settings?.unusedColor,
    unusedSinceInputValid,
    proposedUnusedIso,
    settings?.unusedSinceIso,
  ]);

  useEffect(() => {
    setHasChanges(hasAnyChange());
  }, [hasAnyChange]);

  const handleSave = async () => {
    const payload: {
      newDays?: number;
      newColor?: string;
      unusedColor?: string;
      unusedSinceIso?: string | null;
      cacheTtlHours?: number;
    } = {};
    if (daysValid && parsedDays !== settings?.newDays) payload.newDays = parsedDays;
    if (ttlValid && parsedTtl !== settings?.cacheTtlHours) payload.cacheTtlHours = parsedTtl;
    if (hexValid(newColor) && newColor !== settings?.newColor) payload.newColor = newColor;
    if (hexValid(unusedColor) && unusedColor !== settings?.unusedColor) payload.unusedColor = unusedColor;
    const nextUnused = datetimeLocalValueToIsoOrNull(unusedSinceLocal);
    if (unusedSinceLocal.trim() && nextUnused === null) {
      toastError('Invalid datetime', 'Enter a valid date and time, or clear the field to use all time.');
      return;
    }
    if (
      unusedSinceInputValid &&
      !unusedSinceTimestampsEqual(nextUnused ?? '', settings?.unusedSinceIso ?? '')
    ) {
      payload.unusedSinceIso = nextUnused;
    }
    if (Object.keys(payload).length === 0) return;
    try {
      await updateSettings.mutateAsync(payload);
      setHasChanges(false);
      toastSuccess('Settings saved', 'List tag settings updated.');
    } catch (e) {
      toastError('Save failed', getApiErrorMessage(e, 'Could not save settings'));
    }
  };

  const handleNewDaysBlur = () => {
    if (daysValid && parsedDays !== settings?.newDays) setHasChanges(true);
  };

  const handleNewDaysChange = (value: string) => setNewDays(value);

  const handleTtlBlur = () => {
    if (ttlValid && parsedTtl !== settings?.cacheTtlHours) setHasChanges(true);
  };

  const handleTtlChange = (value: string) => setCacheTtlHours(value);

  const handleCacheEnabledChange = async (enabled: boolean) => {
    try {
      await updateSettings.mutateAsync({ cacheEnabled: enabled });
      toastSuccess('Cache updated', enabled ? 'List tag cache enabled.' : 'List tag cache disabled.');
    } catch (e) {
      toastError('Update failed', getApiErrorMessage(e, 'Could not update cache'));
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border/50 bg-muted/20 px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground">System tags</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Built-in tags shown on leads lists. New and Unused are always visible.
            </p>
          </div>
          {hasChanges && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSave}
              disabled={updateSettings.isPending}
              className="shrink-0"
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              Save changes
            </Button>
          )}
        </div>
      </div>
      <div className="divide-y divide-border">
        {/* New tag */}
        <div className="px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex rounded-md px-2.5 py-1 text-xs font-medium"
                  style={{ backgroundColor: `${hexValid(newColor) ? newColor : '#3b82f6'}20`, color: hexValid(newColor) ? newColor : '#3b82f6' }}
                >
                  New
                </span>
                <span className="text-sm text-muted-foreground">
                  Lists first seen in catalog within last
                </span>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Adjust how many days after first-seen a list shows the New tag.
              </p>
            </div>
            <div className={cn('flex shrink-0 flex-wrap items-center gap-4', isLoading && 'pointer-events-none opacity-60')}>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Threshold</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={newDays}
                    onChange={(e) => handleNewDaysChange(e.target.value)}
                    onBlur={handleNewDaysBlur}
                    disabled={isLoading}
                    className="h-9 w-20 text-center"
                    aria-label="New tag days"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Color</label>
                <ColorPicker
                  color={hexValid(newColor) ? newColor : '#3b82f6'}
                  onChange={setNewColor}
                  placeholder="#3b82f6"
                  presets={TAG_COLOR_PRESETS}
                  className="h-9 w-[140px]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Unused tag — same density as New row */}
        <div className="px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
            <div className="min-w-0 max-w-xl flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex rounded-md px-2.5 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: `${hexValid(unusedColor) ? unusedColor : '#6b7280'}20`,
                    color: hexValid(unusedColor) ? unusedColor : '#6b7280',
                  }}
                >
                  Unused
                </span>
                <span className="text-sm text-muted-foreground">No send since first_seen_at</span>
              </div>
              <p className="mt-1.5 text-xs leading-snug text-muted-foreground">
                Blank From: all unused lists. With From: unused lists first seen on or after that time. Local time,
                stored UTC.
              </p>
            </div>
            <div
              className={cn(
                'flex w-full shrink-0 flex-col gap-5 border-t border-border/60 pt-4 sm:w-auto sm:flex-row sm:items-end sm:gap-8 sm:border-t-0 sm:pt-0 lg:border-l lg:border-border/60 lg:pl-6',
                isLoading && 'pointer-events-none opacity-60',
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:max-w-[min(100%,280px)] lg:pr-8">
                <label
                  className="text-xs font-medium leading-none text-muted-foreground"
                  htmlFor="unused-since-local"
                >
                  From
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    id="unused-since-local"
                    type="datetime-local"
                    value={unusedSinceLocal}
                    onChange={(e) => setUnusedSinceLocal(e.target.value)}
                    className="h-9 min-w-0 flex-1 font-mono text-xs sm:max-w-[240px]"
                    disabled={isLoading}
                    aria-invalid={!unusedSinceInputValid}
                    aria-label="Minimum first_seen_at for Unused; empty means all lists"
                  />
                  {unusedSinceLocal.trim() ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0 px-3 text-xs font-normal"
                      onClick={() => setUnusedSinceLocal('')}
                    >
                      Clear
                    </Button>
                  ) : null}
                </div>
                {!unusedSinceInputValid ? (
                  <span className="text-xs text-destructive" role="alert">
                    Invalid date/time
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5 sm:w-[148px] sm:shrink-0">
                <label className="text-xs font-medium leading-none text-muted-foreground" htmlFor="unused-color-picker">
                  Color
                </label>
                <ColorPicker
                  triggerId="unused-color-picker"
                  color={hexValid(unusedColor) ? unusedColor : '#6b7280'}
                  onChange={setUnusedColor}
                  placeholder="#6b7280"
                  presets={TAG_COLOR_PRESETS}
                  className="h-9 w-full sm:w-[140px]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cache settings */}
        <div className="border-t border-border/50 bg-muted/5 px-6 py-5">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">List tag cache</h3>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Cache performance tags to reduce Voluum API calls. Invalidated on rule or settings change.
          </p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Switch
                id="cache-enabled"
                checked={settings?.cacheEnabled ?? true}
                onCheckedChange={handleCacheEnabledChange}
                disabled={isLoading || updateSettings.isPending}
              />
              <label htmlFor="cache-enabled" className="text-sm font-medium">
                Enable cache
              </label>
            </div>
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
              <label className="text-xs font-medium text-muted-foreground sm:sr-only">TTL</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={CACHE_TTL_MIN}
                  max={CACHE_TTL_MAX}
                  value={cacheTtlHours}
                  onChange={(e) => handleTtlChange(e.target.value)}
                  onBlur={handleTtlBlur}
                  disabled={isLoading}
                  className="h-9 w-20 text-center"
                  aria-label="Cache TTL hours"
                />
                <span className="text-sm text-muted-foreground">hours</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
